import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Stack, Typography,
} from '@mui/material';
import { TxHashLink } from './common';

export interface TxResult {
  status: 'COMPLETED' | 'FAILED';
  txHash?: string | null;
  revertReason?: string | null;
}

/** 서버가 돌려준 단계별 결과 (api/src/common/steps.ts 와 대응) */
export interface TxStep {
  key: string;
  label: string;
  status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'SKIPPED';
  txHash?: string | null;
  detail?: string | null;
}

/** 진행 전 화면이 아는 '계획된 단계' — 무엇을 하는 작업인지 미리 보여준다 */
export interface TxPlanStep {
  key: string;
  label: string;
}

const MARK: Record<TxStep['status'], { icon: string; color: string }> = {
  PENDING: { icon: '', color: '#98a2b3' },
  RUNNING: { icon: '', color: '#2d7ff9' },
  COMPLETED: { icon: '✓', color: '#12b58c' },
  FAILED: { icon: '✕', color: '#c0362f' },
  SKIPPED: { icon: '–', color: '#98a2b3' },
};

function StepRow({ index, label, step }: { index: number; label: string; step?: TxStep }) {
  const st = step?.status;
  const mark = step ? MARK[step.status] : null;
  const running = st === 'RUNNING';
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ py: 0.75 }}>
      <Box
        sx={{
          width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
          display: 'grid', placeItems: 'center', fontSize: 13, fontWeight: 800,
          bgcolor: mark && mark.icon ? `${mark.color}1a` : '#eef1f6',
          color: mark ? mark.color : '#98a2b3',
        }}
      >
        {running ? <CircularProgress size={13} thickness={6} /> : (mark?.icon || index + 1)}
      </Box>
      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Typography variant="body2" sx={{
          fontWeight: st === 'FAILED' || running ? 700 : 500,
          color: st === 'PENDING' ? 'text.secondary' : 'text.primary',
        }}>
          {label}
          {running && <Chip size="small" color="primary" label="진행 중" sx={{ ml: 1, height: 18, fontSize: 11 }} />}
          {st === 'SKIPPED' && (
            <Chip size="small" label="미진행" sx={{ ml: 1, height: 18, fontSize: 11 }} />
          )}
        </Typography>
        {step?.detail && (
          <Typography variant="caption" sx={{ color: step.status === 'FAILED' ? 'error.main' : 'text.secondary', wordBreak: 'break-all' }}>
            {step.detail}
          </Typography>
        )}
        {step?.txHash && <Box sx={{ mt: 0.25 }}><TxHashLink hash={step.txHash} /></Box>}
      </Box>
    </Stack>
  );
}

/**
 * 트랜잭션 진행/결과 모달 (docs/04 TxProgressModal).
 *
 * `plan` 은 화면이 아는 계획 단계, `steps` 는 서버가 보고한 실제 상태다.
 * `useOperationProgress` 로 폴링하면 진행 중에도 RUNNING/COMPLETED 가 실시간 갱신되고,
 * 응답이 오면 확정 결과(성공·실패·미진행·단계별 트랜잭션 해시)로 마무리된다.
 * 폴링을 쓰지 않는 화면은 계획만 표시되다가 완료 후 결과로 대체된다(허위 진행 표시 없음).
 */
export function TxProgressDialog({
  open, title, pending, result, error, onClose, plan, steps,
}: {
  open: boolean;
  title: string;
  pending: boolean;
  result: TxResult | null;
  error: string | null;
  onClose: () => void;
  plan?: TxPlanStep[];
  steps?: TxStep[] | null;
}) {
  const byKey = new Map((steps ?? []).map((s) => [s.key, s]));
  // 계획에 없는 단계(예: 발행자 agent 부여)도 빠뜨리지 않고 뒤에 잇는다
  const rows: { key: string; label: string; step?: TxStep }[] = [
    ...(plan ?? []).map((p) => ({ key: p.key, label: byKey.get(p.key)?.label ?? p.label, step: byKey.get(p.key) })),
    ...(steps ?? []).filter((s) => !(plan ?? []).some((p) => p.key === s.key)).map((s) => ({ key: s.key, label: s.label, step: s })),
  ];

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers={rows.length > 0}>
        {rows.length > 0 && (
          <Box sx={{ mb: pending || error || result ? 2 : 0 }}>
            {rows.map((r, i) => (
              <StepRow key={r.key} index={i} label={r.label} step={r.step} />
            ))}
          </Box>
        )}

        {pending && (
          <Stack direction="row" alignItems="center" spacing={1.5} py={1}>
            {!rows.some((r) => r.step?.status === 'RUNNING') && <CircularProgress size={20} />}
            <Typography variant="body2" color="text.secondary">
              서명(KMS) · 제출 · 확정 대기 중… 온체인 확정까지 수십 초가 걸릴 수 있습니다.
            </Typography>
          </Stack>
        )}
        {!pending && error && <Alert severity="error">{error}</Alert>}
        {!pending && !error && result?.status === 'COMPLETED' && (
          <Alert severity="success">
            처리 완료되었습니다.
            {!rows.length && <div style={{ marginTop: 6 }}><TxHashLink hash={result.txHash ?? null} /></div>}
          </Alert>
        )}
        {!pending && !error && result?.status === 'FAILED' && (
          <Alert severity="error">트랜잭션 실패: {result.revertReason ?? '사유 미상'}</Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending}>{pending ? '처리중…' : '닫기'}</Button>
      </DialogActions>
    </Dialog>
  );
}
